import { c as _c } from "react/compiler-runtime";
import { Calendar } from '@/components/Molecules/Calendar/Calendar';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { useSearchContext } from '@/contexts/SearchContext';
import { getFormattedDateString } from '@/utils/dateUtils';
import type { FilterBounds } from 'bnef';
import { getMonth, isAfter, isBefore, isValid, parseISO, setMonth, startOfMonth } from 'date-fns';
import useTranslation from '@/hooks/useTranslation';
import type { ComponentProps, FC } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
export const DateRangeFilter: FC<{
  name: string;
  min?: string;
  max?: string;
}> = t0 => {
  const $ = _c(88);
  const {
    name,
    min,
    max
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    activeFilters,
    setActiveFilter
  } = useSearchContext();
  const activeFilter = activeFilters[name] as FilterBounds | undefined;
  const [currentValueFrom, setCurrentValueFrom] = useState(activeFilter?.gte ?? "");
  const [currentValueTo, setCurrentValueTo] = useState(activeFilter?.lte ?? "");
  let t1;
  if ($[0] !== currentValueFrom) {
    t1 = () => currentValueFrom ? parseISO(currentValueFrom) : undefined;
    $[0] = currentValueFrom;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [fieldValueFrom, setFieldValueFrom] = useState(t1);
  let t2;
  if ($[2] !== currentValueTo) {
    t2 = () => currentValueTo ? parseISO(currentValueTo) : undefined;
    $[2] = currentValueTo;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [fieldValueTo, setFieldValueTo] = useState(t2);
  let t3;
  if ($[4] !== min) {
    t3 = min && parseISO(min.toString());
    $[4] = min;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const minValue = t3;
  let t4;
  if ($[6] !== max) {
    t4 = max && parseISO(max.toString());
    $[6] = max;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const maxValue = t4;
  let t5;
  if ($[8] !== currentValueFrom || $[9] !== currentValueTo) {
    t5 = () => {
      if (currentValueTo) {
        const parsedTo = parseISO(currentValueTo);
        return startOfMonth(setMonth(parsedTo, getMonth(parsedTo) - 1));
      }
      if (currentValueFrom) {
        return startOfMonth(parseISO(currentValueFrom));
      }
    };
    $[8] = currentValueFrom;
    $[9] = currentValueTo;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const [visibleDateState, setVisibleDate] = useState(t5);
  let t6;
  if ($[11] !== maxValue || $[12] !== minValue) {
    t6 = () => {
      setVisibleDate(prevState => {
        if (!prevState) {
          const today = new Date();
          if (maxValue && isAfter(today, maxValue)) {
            return startOfMonth(setMonth(maxValue, getMonth(maxValue) - 1));
          }
          if (minValue && isBefore(today, minValue)) {
            return startOfMonth(minValue);
          }
          return startOfMonth(today);
        }
        return prevState;
      });
    };
    $[11] = maxValue;
    $[12] = minValue;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let visibleDate = visibleDateState;
  if (!visibleDate && minValue) {
    let t8;
    if ($[15] !== minValue) {
      t8 = startOfMonth(minValue);
      $[15] = minValue;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    visibleDate = t8;
  }
  if (!visibleDate && maxValue) {
    let t8;
    if ($[17] !== maxValue) {
      t8 = startOfMonth(maxValue);
      $[17] = maxValue;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    visibleDate = t8;
  }
  if (!visibleDate) {
    let t8;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = new Date(new Date().getFullYear(), 0, 1, 12, 0, 0);
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    visibleDate = t8;
  }
  let t8;
  if ($[20] !== name || $[21] !== setActiveFilter) {
    t8 = (from, to) => {
      if (!from && !to) {
        setActiveFilter(name, "");
        return;
      }
      const filterValue = {};
      if (from) {
        filterValue.gte = from;
      }
      if (to) {
        filterValue.lte = to;
      }
      setActiveFilter(name, filterValue);
    };
    $[20] = name;
    $[21] = setActiveFilter;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const updateValues = t8;
  let t9;
  if ($[23] !== currentValueFrom || $[24] !== currentValueTo || $[25] !== updateValues) {
    t9 = newDate => {
      const parsedCurrent = parseISO(currentValueFrom);
      if (!currentValueFrom || currentValueTo || newDate < parsedCurrent) {
        const from_0 = getFormattedDateString(newDate);
        setCurrentValueFrom(from_0);
        setFieldValueFrom(newDate);
        setCurrentValueTo("");
        setFieldValueTo(undefined);
        updateValues(from_0, "");
        return;
      }
      const to_1 = getFormattedDateString(newDate);
      setCurrentValueTo(to_1);
      setFieldValueTo(newDate);
      updateValues(currentValueFrom, to_1);
    };
    $[23] = currentValueFrom;
    $[24] = currentValueTo;
    $[25] = updateValues;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const setDate = t9;
  const t10 = getMonth(visibleDate) + 1;
  let t11;
  if ($[27] !== t10 || $[28] !== visibleDate) {
    t11 = setMonth(visibleDate, t10);
    $[27] = t10;
    $[28] = visibleDate;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const nextMonth = t11;
  let t12;
  if ($[30] !== currentValueFrom || $[31] !== currentValueTo) {
    t12 = currentValueFrom && currentValueTo ? {
      from: currentValueFrom,
      to: currentValueTo
    } : undefined;
    $[30] = currentValueFrom;
    $[31] = currentValueTo;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const range = t12;
  const t13 = fieldValueFrom && !fieldValueTo ? fieldValueFrom : undefined;
  let t14;
  if ($[33] !== maxValue || $[34] !== minValue || $[35] !== range || $[36] !== setDate || $[37] !== t13 || $[38] !== visibleDate) {
    t14 = <Calendar visibleDate={visibleDate} setVisibleDate={setVisibleDate} onDateSelect={setDate} min={minValue} max={maxValue} includeForwardButtons={false} selectedRange={range} selectedDate={t13} />;
    $[33] = maxValue;
    $[34] = minValue;
    $[35] = range;
    $[36] = setDate;
    $[37] = t13;
    $[38] = visibleDate;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = fieldValueFrom && !fieldValueTo ? fieldValueFrom : undefined;
  let t16;
  if ($[40] !== maxValue || $[41] !== minValue || $[42] !== nextMonth || $[43] !== range || $[44] !== setDate || $[45] !== t15) {
    t16 = <Calendar visibleDate={nextMonth} setVisibleDate={setVisibleDate} onDateSelect={setDate} min={minValue} max={maxValue} includeBackwardButtons={false} selectedRange={range} selectedDate={t15} />;
    $[40] = maxValue;
    $[41] = minValue;
    $[42] = nextMonth;
    $[43] = range;
    $[44] = setDate;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t14 || $[48] !== t16) {
    t17 = <Calendars>{t14}{t16}</Calendars>;
    $[47] = t14;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t) {
    t18 = t("SearchFilter.filterNames.from");
    $[50] = t;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== fieldValueFrom) {
    t19 = fieldValueFrom ? getFormattedDateString(fieldValueFrom) : "";
    $[52] = fieldValueFrom;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = `${name}[gte]`;
  let t21;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = e => {
      try {
        const date = parseISO(e.target.value);
        if (isValid(date)) {
          setFieldValueFrom(date);
          setVisibleDate(startOfMonth(date));
        }
      } catch {}
    };
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== currentValueTo || $[56] !== fieldValueFrom || $[57] !== updateValues) {
    t22 = () => {
      const from_1 = fieldValueFrom ? getFormattedDateString(fieldValueFrom) : "";
      setCurrentValueFrom(from_1);
      updateValues(from_1, currentValueTo);
    };
    $[55] = currentValueTo;
    $[56] = fieldValueFrom;
    $[57] = updateValues;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== max || $[60] !== min || $[61] !== t18 || $[62] !== t19 || $[63] !== t20 || $[64] !== t22) {
    t23 = <input aria-label={t18} type="date" value={t19} name={t20} min={min} max={max} onChange={t21} onBlur={t22} />;
    $[59] = max;
    $[60] = min;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t) {
    t24 = t("SearchFilter.filterNames.to");
    $[66] = t;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== fieldValueTo) {
    t25 = fieldValueTo ? getFormattedDateString(fieldValueTo) : "";
    $[68] = fieldValueTo;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  const t26 = `${name}[lte]`;
  let t27;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = e_0 => {
      try {
        const date_0 = parseISO(e_0.target.value);
        if (isValid(date_0)) {
          setFieldValueTo(date_0);
          setVisibleDate(startOfMonth(date_0));
        }
      } catch {}
    };
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== currentValueFrom || $[72] !== fieldValueTo || $[73] !== updateValues) {
    t28 = () => {
      const to_2 = fieldValueTo ? getFormattedDateString(fieldValueTo) : "";
      setCurrentValueTo(to_2);
      updateValues(currentValueFrom, to_2);
    };
    $[71] = currentValueFrom;
    $[72] = fieldValueTo;
    $[73] = updateValues;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== max || $[76] !== min || $[77] !== t24 || $[78] !== t25 || $[79] !== t26 || $[80] !== t28) {
    t29 = <input aria-label={t24} type="date" value={t25} name={t26} min={min} max={max} onChange={t27} onBlur={t28} />;
    $[75] = max;
    $[76] = min;
    $[77] = t24;
    $[78] = t25;
    $[79] = t26;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== t23 || $[83] !== t29) {
    t30 = <Fields>{t23}{t29}</Fields>;
    $[82] = t23;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== t17 || $[86] !== t30) {
    t31 = <Stack>{t17}{t30}</Stack>;
    $[85] = t17;
    $[86] = t30;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  return t31;
};
const Calendars = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--size-grid-unit);
`;
const Fields = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--size-grid-half);

    input {
        width: fit-content;
        text-align: center;
        text-transform: uppercase;
        -moz-appearance: none;
        appearance: none;
        // todo - disable calendar button on desktop
    }

    input[type='date']::-webkit-inner-spin-button,
    input[type='date']::-webkit-calendar-picker-indicator {
        display: none !important;
        -webkit-appearance: none !important;
    }
`;